<template lang="html">
<div class="h-full">
  <div class="w-full">
    <!-- Placeholder -->
    <div class="relative w-full bg-black rounded overflow-hidden" style="padding-bottom: 56.25%" id="viewportPlaceholderImage" :class="{'fullscreen': isInFullscreen}"
         @drop="onDrop($event)" @dragstart.prevent="" @dragover.prevent="" @dragenter.prevent="" @click="enterFullscreen">
      <div v-if="!isInEditor && showCurtain" class="bg-red-600 text-white text-center py-2 w-full top-0 absolute z-40">Curtain is active</div>
      <div v-if="!isInEditor && !isPointersActive" class="right-0 absolute z-10 text-white bg-black opacity-100 hover:opacity-75 px-2 py-1">
        <div class="flex justify-between">
          <div>
            Status
          </div>
        <button @click="showPeerStatus = !showPeerStatus" class="pl-4 hover:underline">
          <div v-if="!showPeerStatus">Show</div>
          <div v-else>Hide</div>
          </button>
        </div>
        <table class="text-white" v-if="showPeerStatus">
          <thead v-if="this.slides"><td></td>
            <td v-for="slide in this.slides.length">
              <div :class="slide == (currentSlideIndex + 1) ? 'text-blue-300 font-bold' : 'text-gray-200'">
                {{ slide }}
              </div>
            </td>
          </thead>
          <tr v-for="peer in participants" class="text-gray-100" v-if="peer">
            <td>{{peer.name}}</td>
            <td v-for="slide in peer.slides">
              <span v-if="slide.percentage == 100">✅</span>
              <span v-else> {{  parseInt(slide.percentage) }} </span>
            </td>
          </tr>
        </table>
      </div>
      <template v-if="(typeof currentMedia) !== 'undefined' && !isSharingScreen">
        <transition :name="selectedAnim">
          <template v-if="currentState === 0 || isLoading" >
            <div class="bg-black opacity-75 text-white flex space-y-2 flex-col absolute z-10 inset-0 items-center justify-center">
  			      <!-- Spinning semi-circle -->
  				      <svg class="animate-spin  h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
  					      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
  					      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  				      </svg>
  				  <h1>
              Loading clip <span class="font-mono">{{ loadProgress }}</span>
              <div class="text-sm text-gray-600 mt-5 font-mono"> {{ detailedProgress }}</div>
  				  </h1>
  				  </div>	
          </template>
          <template v-else-if="currentMedia && currentMedia.media_type === 'image'">
            <img :key="mediaInDisplay" class="absolute w-full h-full object-contain"
                 :src="mediaInDisplay">
          </template>
          <template v-else-if="currentMedia && currentMedia.media_type === 'audio'">
            <div class="absolute bg-black h-full w-full">
              <div class="text-white absolute left-10 top-10">
                <div class="h-32 w-32 rounded bg-animated flex items-center justify-center text-sm text-white">
                  <div>
                    Artwork
                  </div>
                </div>
                <div class="text-lg font-bold mt-2">
                  Audio file
                </div>
                <div class="italic">
                  {{ currentMedia.name || "No title"}}
                </div>
                <div class="text-gray-100">
                  {{ "00:00" }}
                  / {{ "01:00" }}
                </div>
                <div id="randomAudioBits" class="vt323">
                  ......
                </div>
              </div>
              <audio :key="mediaInDisplay" controls id="player" :src="mediaInDisplay" class="absolute left-0 bottom-0 w-full h-full object-contain p-2"></audio>
            </div>
          </template>
          <template v-else-if="currentMedia && currentMedia.media_type === 'video'">
            <video :key="mediaInDisplay" controls id="player" :src="mediaInDisplay" class="absolute w-full h-full object-contain"></video>
          </template>
        </transition>
      </template>
      <video autoplay playsinline muted id="shareScreenVideo" class="hidden absolute w-full h-full object-contain"></video>
    </div>
    <div class="flex space-x-2 justify-between py-1">
      <div class="rounded-md h-1 flex-grow" :class="{'bg-primary': i <= currentSlideIndex + 1, 'bg-gray-200': i > currentSlideIndex + 1}" v-for="i in slides.length"></div>
    </div>
    <div class="mt-2 grid grid-cols-3 gap-4">
      <button v-if="!isInEditor && slides[currentSlideIndex-1] !== undefined" v-on:click="prevSlide" class="flex items-center text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md">
		    <!-- chevron left -->
		    <svg class="w-8 h-8 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M12.452 4.516c.446.436.481 1.043 0 1.576L8.705 10l3.747 3.908c.481.533.446 1.141 0 1.574-.445.436-1.197.408-1.615 0-.418-.406-4.502-4.695-4.502-4.695a1.095 1.095 0 0 1 0-1.576s4.084-4.287 4.502-4.695c.418-.409 1.17-.436 1.615 0z"/></svg>
        Previous
      </button>
      <div v-else>
      </div>
      <div class="flex justify-center items-center text-gray-700">
		    Slide: <span class="text-gray-900">{{currentSlideIndex + 1}}/{{slides.length}}</span>
        <div v-if="!isInEditor && slides[currentSlideIndex] !== undefined" class="px-2 text-gray-400">
          <h5 v-if="slides[currentSlideIndex].name !== undefined && slides[currentSlideIndex].name !== null">{{slides[currentSlideIndex].name}}</h5>
          <h5 v-else class="italic">(No Title)</h5>
        </div>
        <div v-else>
          <template v-if="slides[currentSlideIndex] !== undefined">
            <input v-model="slides[currentSlideIndex].name" class="ml-4" type="text" placeholder="Slide Title">
          </template>
        </div>
      </div>
      <button v-if="!isInEditor && slides[currentSlideIndex+1] !== undefined" v-on:click="nextSlide" class="flex justify-end items-center hover:bg-gray-200 hover:text-gray-900 text-gray-600 active:bg-primary">
          Next
        <div v-if="!isInEditor && slides[currentSlideIndex+1] !== undefined" class="px-2 text-gray-400">
          <h5 v-if="slides[currentSlideIndex+1].name !== undefined && slides[currentSlideIndex+1].name !== null">({{slides[currentSlideIndex+1].name}})</h5>
          <h5 v-else class="italic">(No Title)</h5>
        </div>
		<!-- chevron right -->
	  <svg class="w-8 h-8 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.163 4.516c.418.408 4.502 4.695 4.502 4.695a1.095 1.095 0 0 1 0 1.576s-4.084 4.289-4.502 4.695c-.418.408-1.17.436-1.615 0-.446-.434-.481-1.041 0-1.574L11.295 10 7.548 6.092c-.481-.533-.446-1.141 0-1.576.445-.436 1.197-.409 1.615 0z"/></svg>
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import commandsManager from 'general/CommandsManager'
import messageBroker from 'general/messageBroker';
import Pointers from 'presenter/pointers';
import SlidesLoader from 'presenter/slides-loader';

export default {
  name: "presenter-viewport",
  props: {
    roomActivated: {
      type: Boolean,
      required: false
    }, 
    isInEditor: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSharingScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    roomKey: {
      type: String,
      required: true,
    },
    currentSlideIndex: {
      type: Number,
      required: true,
      default: -1,
    },
    presentationId: {
      type: Number,
      required: true
    },
    selectedAnim: '',
  },
  methods: {
    resetPresentedMedia(){
      this.presentedMedia = null;
    },
    setKeyboardEvents(){
      document.addEventListener("keyup", (e) => {
        // 37 left
        // 38 up
        // 39 right
        // 40 down
        if (e.keyCode == 37 || e.keyCode == 38){
          this.prevSlide();
        }
        if (e.keyCode == 39 || e.keyCode == 40){
          this.nextSlide();
        }
        if (e.keyCode == 27){ // esc key
          this.resetPresentedMedia();
          commandsManager.sendAllCommand('finish_presenting_media')
        }
      });
    },
    resizeVideo() {
      const videoEl = $('#localVideo');
      const videoElWrapper = $('#localVideo').parents('.plyr--video');
      ;
      const rotation = parseInt(this.angle);
      if (rotation == 0 || rotation == 180) {
        videoEl.width(videoElWrapper.width());
      }
      if (rotation == 90 || rotation == 270) {
        videoEl.width(videoElWrapper.height());
      }
    },
    nextSlide() {
      if (this.slides.length - 1 === this.currentSlideIndex) return;
      this.$emit("update:current-slide-index", this.currentSlideIndex + 1);
      this.updateCurrentMedia();
    },
    prevSlide() {
      if (0 === this.currentSlideIndex) return;
      this.$emit("update:current-slide-index", this.currentSlideIndex - 1);
      this.updateCurrentMedia();
    },
    addNewParticipant(participant){
      // check if participant is already in the list
      for (var i = 0; i < this.participants.length; i++){
        if (this.participants[i].public_key == participant.public_key){
          return;
        }
      }
      if (participant.slides === undefined) {
        participant.slides = [];
        for(let i = 0; i < this.slides.length; i++){
          participant.slides.push({ status: 'LOADING', percentage: 0 });
        }
      }
      this.participants.push(participant)
      // request status to participant
      messageBroker.sendCommand('presenter--request-slides-status', participant.public_key)
    },
    updateParticipant(participant){
      let found = false;
      for (var i = 0; i < this.participants.length; i++){
        if (this.participants[i].public_key == participant.public_key){
          found = true;
          this.participants[i] = participant;
          this.participants[i].slides = [];
          for(let j = 0; j < this.slides.length; j++){
              this.participants[i].slides.push({ status: 'LOADING', percentage: 0 });
          }
          this.participants.push({});
          this.participants.pop();
        }
      }
      if (!found){
        this.addNewParticipant(participant);
      }
    },
    updateCurrentMedia(){
      // TODO: check if room is activated to send this command
      if(this.presentedMedia === null){ // normal slide change
        // Send slide to all peers

        if (this.showCurtain == false) {
          commandsManager.sendAllCommand('slide_changed', this.currentSlideIndex)
        }

        this.currentState = this.slidesLoader.getState(this.currentSlideIndex)
        this.isLoading = true;
        let currentSlideIndex = this.currentSlideIndex;
        let startTimestamp = new Date().getTime(); 
        let avgSpeed = 0;
        this.slidesLoader.get(this.currentSlideIndex, (loadProgress, loaded, total)=>{
          if (this.currentSlideIndex != currentSlideIndex) return
          let timeSinceStart = new Date().getTime() - startTimestamp;
          if (timeSinceStart > 0){
            avgSpeed = loaded / timeSinceStart;
          }
          console.log("loading", loadProgress, loaded, total, timeSinceStart, avgSpeed);
          this.loadProgress =  loadProgress.toFixed(2) + "%"
          this.detailedProgress = (loaded/1024/1024).toFixed(2) + "/" + (total/1024/1024).toFixed(2) + "MB " + (timeSinceStart/1000).toFixed(2) + "s" + " " + (avgSpeed/1024).toFixed(2) + "MB/s";
        }).then((media) => {
          if (this.currentSlideIndex != currentSlideIndex) return
          this.currentMedia = media;
          this.mediaInDisplay = this.currentMedia.url;
          this.currentType = this.currentMedia.media_type;
          this.setUpSenderForAudioVideoMedia();
          this.setQuietAudio();
          this.isLoading = false;
        }).catch((error) => {
          console.error("Error loading media", error);
          this.isLoading = false;
        });
      } else { // media has been presented
        this.currentMedia = Object.assign({}, this.presentedMedia)
        this.mediaInDisplay = this.currentMedia.url;
        this.currentType = this.currentMedia.media_type;
        this.setUpSenderForAudioVideoMedia();
        this.setQuietAudio();
      }
    },
    setQuietAudio(){
      window.setTimeout(() => {
        if(this.currentType === 'audio' || this.currentType === 'video'){
          let player = document.getElementById('player');
          if (player == null) return;
          player.volume = this.isQuietAudioActive ? 0.1 : 1;
        }
      }, 1000)
    },
    setUpSenderForAudioVideoMedia() {
     if (this.isInEditor) { return }

      if ((typeof this.currentType) === 'undefined')
        return
      console.log('Setting up for: ' + this.currentType);
      if(this.currentType === 'audio' || this.currentType === 'video'){
        window.setTimeout(() => {
          let player = document.getElementById('player');
          if(player == null) return;
          player.onplay = (event) => {
            console.log('play', this, this.showCurtain)
            if (this.showCurtain == false ){
              commandsManager.sendAllCommand('play_video_clip');
            }
            // to avoid double calling this, this is weird however
            if (this.playInterval != null) return;
            this.playingInterval = window.setInterval(()=> {
              let string = "."
              for (var i = 0; i < 6; i ++){
                const rand = Math.random();
                string += rand > 0.5 + (((i / 2) + 3) / 10) ? "|" : "."
              }
              string +="."
              $("#randomAudioBits").html(string)
            }, 100)
          }
          player.onpause = (event) => {
            // console.log('pause')
            $("#randomAudioBits").html(".....")
            if (this.playingInterval != null){
              window.clearInterval(this.playingInterval);
              this.playingInterval = null;
            }
            if (this.showCurtain == false ){
              commandsManager.sendAllCommand('pause_video_clip');
            }

            let currentTime = event.currentTarget.currentTime;
            // this timeout is to ensure that the settimevideoclip is after the pause
            window.setTimeout(()=>{
              // update so that everyone sees the same frame
              if (this.showCurtain == false) {
                commandsManager.sendAllCommand("set_time_video_clip", { currentTime: currentTime });
              }
            }, 500);
          }
          player.onseeking = (event) => {
            // console.log("event seeked", event);
            if (this.showCurtain == false ){
              commandsManager.sendAllCommand('set_time_video_clip', { currentTime: event.currentTarget.currentTime });
            }
          }
        }, 1000)
      }
    },

    onDrop(e){
      if (this.isInEditor) {
        return
      }

      const draggingType = e.dataTransfer.getData('type');
      // Media
      if(draggingType === "MEDIA"){ // present media
        let media = JSON.parse(e.dataTransfer.getData('media'));
        // console.log("media", media) 
        commandsManager.sendAllCommand('presenting_media', media);
        this.presentedMedia = media;
        /// set listeners
        this.setUpSenderForAudioVideoMedia();
        this.updateCurrentMedia();
      }
    },
    setupCommands(){
     if (this.isInEditor) { return }
     commandsManager.registerNewCommand('play_video_clip', (data) => {
            // console.log('play received')
            $("#player")[0].play();
          })
          commandsManager.registerNewCommand('pause_video_clip', (data) => {
            // console.log('pause recived')
            $("#player")[0].pause();
          })
          commandsManager.registerNewCommand('set_time_video_clip', (data) => {
            var current = $("#player")[0].currentTime;
            // console.log("event seeked recived", current, data.args.currentTime, Math.abs(parseFloat(current) - parseFloat(data.args.currentTime)))
            if(Math.abs(parseFloat(current) - parseFloat(data.args.currentTime)) < 0.5) return;
            $("#player")[0].currentTime = data.args.currentTime;
          })

 /* Pointer commands*/

    commandsManager.registerNewCommand('quiet_audio', ({args: {active}}) => {
      // if active lower the volume 70%
      if(this.currentType === 'audio' || this.currentType === 'video'){
        $("#player")[0].volume = active ? 0.1 : 1;
      }
    })

    // Observe if host or presentators are presenting media
    commandsManager.registerNewCommand('presenting_media', ({args: media}) => {
      this.presentedMedia = media;
    });

    // If presenting media was eneded by escape we should listen this event
    commandsManager.registerNewCommand('finish_presenting_media', () => {
      this.presentedMedia = null;
    })
    },
    enterFullscreen() {
      this.isInFullscreen = !this.isInFullscreen;
    },
    togglePointers(enabled){
      this.isPointersActive = enabled;
      if (enabled == false){
        this.pointers.disablePointers();
      } else {
        this.pointers.enablePointers();
      }
    }
  },
  computed: {
  },
  data() {
    return {
      angle: 0,
      sending: false,
      isInFullscreen: false,

      // for participant status
      participants: [],
      slides: [],
      currentState: 0,
      role: window.role,
      presentedMedia: null,
      isQuietAudioActive: false,
      playingInterval: null,

      fileDuration: null,
      fileCurrentTime: null,
      showPeerStatus: true,
      currentMedia: null,

      isLoading : true,
      loadProgress: "",
      detailedProgress: "",

      pointers: null,
      isPointersActive: false,
      pointersAsList: [],
      showCurtain: true,
      mediaInDisplay: null,
      slidesLoader : null,
    }
  },
  watch: {
    showCurtain(newVal, oldVal){
      if (newVal == false){
        commandsManager.sendAllCommand('slide_changed', this.currentSlideIndex)
        const player = $("#player")[0];
        if (player){
          commandsManager.sendAllCommand('set_time_video_clip', { currentTime: player.currentTime });
        }
      }
    },
    currentSlideIndex(newIndex, oldIndex) {// if slide index change finish presenting media
      console.log("changed slide");
      this.resetPresentedMedia();
      this.updateCurrentMedia();

      // presented media are dropped in media 
      if(this.presentedMedia === null){
        // this.currentMedia = this.slidesLoader.get(this.currentSlideIndex)
      } else {
       this.mediaInDisplay = this.presentedMedia.url;
      }

    },
    isQuietAudioActive(){
      if(this.currentType === 'audio' || this.currentType === 'video'){
        $("#player")[0].volume = this.isQuietAudioActive ? 0.1 : 1;
      }
    },
  },
  mounted() {
    let viewportEl = document.querySelector('#viewportPlaceholderImage');
    this.pointers = new Pointers(viewportEl, myId, window.name || participant_name);
    this.setupCommands();
    this.setKeyboardEvents()
    this.slidesLoader = new SlidesLoader(this.presentationId, !this.isInEditor);
    this.slides = this.slidesLoader.loadSlides().then((slides) => {
      this.slides = slides;
      this.updateCurrentMedia();
      this.$emit("newSlides", slides);
    });

    var self = this;
    // set up status updates from peers
    messageBroker.registerNewMessageType('presenter--playback_status', (data) => {
      console.log("STATUS Playback status", data)
      let status = data.status.toUpperCase();
      let percentage = data.percentage;
      switch (status) {
      case 'READY':
        status += ' ✅';
        break;
      case 'PLAYING':
        status += ' ▶️';
        break;
      case 'PAUSED':
        status += ' ⏸';
        break;
      case 'ENDED':
        status += ' 🏁';
        break;
      case 'LOADING':
        status += '...' + percentage + "%";
        break;
      default:
        break;
      }
      for (var i = 0; i < self.participants.length; i++){
        if (self.participants[i].public_key == data.peer_key){
          let currentSlide = self.participants[i].slides[data.index];
          if (currentSlide){
            if (currentSlide.percentage < percentage || data.status.toUpperCase() == "PLAYING"){
              // update percentage
              currentSlide.percentage = percentage;
              currentSlide.status = status;
            }
          }
          // {
          //   status: status,
          //   percentage: percentage
          // }
          // self.participants[i].slides[data.index] = {
          //   status: status,
          //   percentage: percentage
          // }
          // self.participants[i].slides.push({});
          // self.participants[i].slides.pop();
        }
      }
      this.participants.push({});
      this.participants.pop();
    });

  }
}
</script>

<style scoped>

.fullscreen {
  /* width: 100vw;
     height: 100vw; */
  /* margin: 0;
     padding: 0;
     z-index: 1000;
     top: 0px;
     margin-left: calc(-50vw + 50%); */
     
}
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
